import { Socket } from 'phoenix';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMessage } from '@/hooks/message';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

const SOCKET_HEARTBEAT_INTERVAL_MS = 15_000;
const SOCKET_RECONNECT_INTERVAL_MS = 5_000;
const MAX_AGE_IN_SECONDS = 15;

export function useWebsocket() {
  const userProfile = useMgmtPlaneStateStore((state) => state.userProfile);
  const socket = useMgmtPlaneStateStore((state) => state.socket);
  const setSocket = useMgmtPlaneStateStore((state) => state.setSocket);
  const [message] = useMessage();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (!userProfile?.id) return;
      if (socket) return;

      const { data: tokenInfo } = await mgmtPlaneAPIClient.auth.generateToken({ maxAge: MAX_AGE_IN_SECONDS });
      const { token, tenantID } = tokenInfo;
      connectSocket(token, tenantID, setSocket, navigate);
    })();

    return () => {
      if (!socket) return;
      socket.disconnect();
      setSocket(undefined);
    };
  }, [message, socket, userProfile, setSocket, navigate]);
}

function connectSocket(
  token: string,
  tenantID: string,
  setSocket: (socket: Socket) => void,
  navigate: (to: string, options?: { replace: boolean }) => void,
) {
  const socket = new Socket(`${window.mimicConfig.MIMIC_MGMT_PLANE_API}/api/socket`, {
    params: { token, tenantID },
    heartbeatIntervalMs: SOCKET_HEARTBEAT_INTERVAL_MS,
    reconnectAfterMs: () => SOCKET_RECONNECT_INTERVAL_MS,
  });

  socket.connect();

  socket.onError((err) => {
    // eslint-disable-next-line no-console
    console.warn('Websocket connection error', err);

    mgmtPlaneAPIClient.auth
      .generateToken({ maxAge: MAX_AGE_IN_SECONDS })
      .then(({ data: tokenInfo }) => {
        socket.disconnect();
        socket.connect({ token: tokenInfo.token, tenantID: tokenInfo.tenantID });
      })
      .catch((error) => {
        if (error.status === 401) {
          const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          sessionStorage.setItem('returnTo', returnTo);
          navigate('/login', { replace: true });
        } else {
          // eslint-disable-next-line no-console
          console.warn('Failed to refresh websocket token, relying on automatic reconnect', error);
        }
      });
  });

  socket.onClose(() => {
    // eslint-disable-next-line no-console
    console.log('Websocket connection closed');
  });

  setSocket(socket);
}
